import { useRef, useState, type ReactNode, type Ref } from 'react'
import { Field } from './Field'
import { Input } from './Input'
import { DatePicker } from './DatePicker'
import { Select } from './Select'
import { Switch } from './Switch'
import { MultiSelect } from './MultiSelect'
import { Button } from './Button'
import { Lookup, type LookupFacet, type LookupType, type LookupProps } from './Lookup'
import { RelationField } from './RelationField'
import { cx } from './cx'
import type { Tone } from './Pill'
import s from './Form.module.css'

/**
 * Form — the declarative, metamodel-driven form primitive. The keystone of the
 * relationship experience: quick-create, edge-attribute editing, advanced-search
 * editors and (later) the record page are all Forms. Fields come from a schema
 * (from `core.form`/`field_meta` or a caller subset); the control per type is
 * chosen here. Always-editable (the house rule) — controlled via value/onChange.
 * `relation` fields render the @solum/ui Lookup, so a Form can edit relationships
 * and a relationship can open a Form — the recursion is the system.
 */
export type RelationshipCardinality = 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many'

export interface FormField {
  name: string
  type: 'text' | 'textarea' | 'number' | 'money' | 'date' | 'select' | 'multiselect' | 'switch' | 'relation'
  label: string
  required?: boolean
  /** `selectable: false` ⇒ shown and labelled, closed to a NEW choice (#1075). */
  options?: { value: string; label: string; tone?: Tone; selectable?: boolean }[]
  relation?: {
    target?: string
    multi?: boolean
    search?: LookupProps['search']
    display?: (id: string) => string
    facets?: (q: string) => LookupFacet[]
    placeholder?: string
    types?: LookupType[]
    defaultType?: string
    discriminatorColumn?: string
    /** Cardinality from the relationship declaration. Undefined is not permission for many. */
    cardinality?: RelationshipCardinality
    advanced?: LookupProps['advanced']
    unavailableMessage?: string
  }
  hint?: string
  placeholder?: string
  tag?: string
  colSpan?: 1 | 2
  validate?: (value: unknown, all: Record<string, unknown>) => string | null
}

export interface FormProps {
  fields: FormField[]
  value: Record<string, unknown>
  onChange: (patch: Record<string, unknown>) => void
  onSubmit?: () => void | Promise<void>
  onCancel?: () => void
  submitLabel?: string
  layout?: 'grid' | 'stack'
  variant?: 'full' | 'quick'
  busy?: boolean
  testid?: string
  /**
   * The form element, so a host that owns the primary action can submit THROUGH
   * this form rather than around it (#2351).
   *
   * `requestSubmit()` runs the form's own validation gate — the required-field
   * check above and every field's `validate` — so a surface whose commit button
   * lives somewhere else keeps ONE validation rule instead of a second copy
   * that drifts. The relationship workspace's footer is the case: its single
   * primary action sits beside the edge facts, three regions away from the
   * create form it commits.
   */
  formRef?: Ref<HTMLFormElement>
  /**
   * Draw no action row. ONLY for a host that renders its own submit and wires
   * it to `formRef` — a form with neither an action nor an external one is a
   * form nobody can submit, and this prop cannot tell the difference.
   */
  hideActions?: boolean
}

/** A missing declaration is not permission to offer a many-value write. */
export function relationAllowsMultiple(cardinality: RelationshipCardinality | undefined): boolean {
  return cardinality === 'one_to_many' || cardinality === 'many_to_many'
}

function fieldError(f: FormField, value: Record<string, unknown>): string | null {
  const v = value[f.name]
  const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0)
  if (f.required && empty) return 'Required'
  return f.validate ? f.validate(v, value) : null
}

export function Form({ fields, value, onChange, onSubmit, onCancel, submitLabel, layout = 'grid', variant = 'full', busy, testid, formRef, hideActions }: FormProps) {
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const [tried, setTried] = useState(false)
  const set = (name: string, v: unknown) => onChange({ [name]: v })
  const patch = (valuePatch: Record<string, unknown>) => onChange(valuePatch)
  const touch = (name: string) => setTouched((t) => (t.has(name) ? t : new Set(t).add(name)))
  const shown = (f: FormField) => tried || touched.has(f.name)
  const grid = layout === 'grid' && variant !== 'quick'

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setTried(true)
    if (fields.some((f) => fieldError(f, value))) return
    await onSubmit?.()
  }

  return (
    <form ref={formRef} className={cx(s.form, grid ? s.grid : s.stack)} data-testid={testid} onSubmit={submit} noValidate>
      {fields.map((f) => {
        const err = shown(f) ? fieldError(f, value) : null
        const v = value[f.name]
        return (
          <div key={f.name} className={cx(f.colSpan === 2 && s.span2)}>
            {/* The hint goes THROUGH Field (UX-04). It used to be a sibling
                <div> after it — visible, and invisible to assistive tech,
                because nothing pointed the control's aria-describedby at it.
                Field owns that wiring; rendering guidance outside it means the
                guidance is not part of the control's accessible description. */}
            <Field label={f.label} required={f.required} tag={f.tag}
              error={err ?? undefined} hint={!err ? f.hint : undefined}>
              {control(f, v, value, set, patch, touch)}
            </Field>
          </div>
        )
      })}
      {!hideActions && (onSubmit || onCancel) && (
        <div className={s.actions}>
          {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
          {onSubmit && <Button type="submit" variant="primary" loading={busy}>{submitLabel ?? (variant === 'quick' ? 'Create' : 'Save')}</Button>}
        </div>
      )}
    </form>
  )
}

function control(
  f: FormField, v: unknown, all: Record<string, unknown>,
  set: (n: string, v: unknown) => void,
  patch: (value: Record<string, unknown>) => void,
  touch: (n: string) => void,
): ReactNode {
  const blur = () => {
    /* Autofocus did not enter a value. Showing its Required line when the
       person clicks another control moves that target between pointer down
       and up. Edited fields (including an explicit clear) still validate on
       blur; submit always validates every field. */
    if (!f.required || Object.prototype.hasOwnProperty.call(all, f.name)) touch(f.name)
  }
  switch (f.type) {
    case 'textarea':
      return <textarea className={s.ta} value={String(v ?? '')} placeholder={f.placeholder}
        onChange={(e) => set(f.name, e.target.value)} onBlur={blur} />
    case 'number':
    case 'money':
      return <Input type="number" inputMode="decimal" value={v == null ? '' : String(v)} placeholder={f.placeholder}
        onChange={(e) => set(f.name, e.target.value === '' ? null : Number(e.target.value))} onBlur={blur} />
    case 'date':
      return <DatePicker name={f.name} value={String(v ?? '')} required={f.required}
        onChange={(next) => set(f.name, next)} onBlur={blur} />
    case 'select':
      return <Select value={String(v ?? '')} options={f.options ?? []} placeholder={f.placeholder ?? 'Select…'}
        onValueChange={(nv) => { set(f.name, nv); touch(f.name) }} />
    case 'multiselect':
      return <MultiSelect value={(v as string[]) ?? []} options={f.options ?? []} placeholder={f.placeholder}
        onChange={(nv) => { set(f.name, nv); touch(f.name) }} />
    case 'switch':
      return <Switch checked={!!v} onCheckedChange={(c) => { set(f.name, c); touch(f.name) }} />
    case 'relation':
      return f.relation?.multi && !relationAllowsMultiple(f.relation.cardinality)
        ? <RelationField value={null} onChange={() => undefined} search={() => []}
            unavailableMessage="This relationship does not permit multiple selection." />
        : f.relation?.multi
        ? <RelationControl f={f} v={v} set={set} touch={touch} />
        : <RelationField value={v ? String(v) : null}
            onChange={(id, selectedType) => {
              const discriminator = f.relation?.discriminatorColumn
              if (discriminator && selectedType) {
                const currentType = all[discriminator] == null ? null : String(all[discriminator])
                patch(id == null && currentType === selectedType
                  ? { [f.name]: null }
                  : { [discriminator]: selectedType, [f.name]: id })
                touch(discriminator)
              } else set(f.name, id)
              touch(f.name)
            }}
            search={f.relation?.search ?? (() => [])}
            display={f.relation?.display ? (id) => ({ id, primary: f.relation!.display!(id) }) : undefined}
            facets={f.relation?.facets}
            advanced={f.relation?.advanced}
            types={f.relation?.types}
            defaultType={f.relation?.defaultType}
            onTypeChange={f.relation?.discriminatorColumn
              ? (selectedType) => {
                  patch({ [f.relation!.discriminatorColumn!]: selectedType, [f.name]: null })
                  touch(f.relation!.discriminatorColumn!)
                  touch(f.name)
                }
              : undefined}
            unavailableMessage={f.relation?.unavailableMessage}
            placeholder={f.relation?.placeholder ?? f.placeholder} />
    default:
      return <Input value={String(v ?? '')} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} onBlur={blur} />
  }
}

function RelationControl({ f, v, set, touch }: { f: FormField; v: unknown; set: (n: string, v: unknown) => void; touch: (n: string) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const rel = f.relation ?? {}
  const permitsMany = relationAllowsMultiple(rel.cardinality)
  const multi = rel.multi === true && permitsMany
  const ids = multi ? ((v as string[]) ?? []) : (v ? [String(v)] : [])
  const disp = (id: string) => rel.display?.(id) ?? 'Record unavailable'
  // Keep the cardinality gate outside the governed Lookup callsite. Besides
  // making the omitted handler explicit authority, these named handlers keep
  // the selection-callsite census able to account for both modes.
  const handlePick: LookupProps['onPick'] = multi
    ? undefined
    : (it) => { set(f.name, it.id); touch(f.name) }
  const handleCommit: LookupProps['onCommit'] = multi
    ? (items) => {
        set(f.name, [...new Set([...ids, ...items.map((item) => item.id)])])
        touch(f.name)
      }
    : undefined
  return (
    <>
      {/* The remove affordance was a `span role="button"` with no tabIndex or
          key handler, NESTED inside the field <button> — removing a relation
          chip was impossible without a mouse, and the nesting is invalid HTML
          (#388). The wrapper is a styled div; the chips carry real remove
          buttons and a real trigger opens the lookup. */}
      <div className={cx(s.rel, !ids.length && s.relEmpty)}>
        {ids.length > 0 && (
          <span className={s.relChips}>{ids.map((id) => (
            <span key={id} className={s.relChip}>◎ {disp(id)}
              <button type="button" aria-label={`Remove ${disp(id)}`} className={s.relX}
                onClick={() => set(f.name, multi ? ids.filter((x) => x !== id) : null)}>×</button>
            </span>))}</span>
        )}
        <button ref={ref} type="button" className={s.relOpen} disabled={!!rel.unavailableMessage}
          onClick={() => rel.search && !rel.unavailableMessage && setOpen(true)}>
          {rel.unavailableMessage ? 'Relationship unavailable'
            : ids.length ? '＋ Add' : <span>◎ {rel.placeholder ?? 'Search…'}</span>}
        </button>
      </div>
      {rel.unavailableMessage && <small role="status">{rel.unavailableMessage}</small>}
      {open && rel.search && !rel.unavailableMessage && (
        <Lookup presentation="popover" anchor={ref.current} search={rel.search} advanced={rel.advanced}
          mode={multi ? 'multi' : 'single'}
          onClose={() => setOpen(false)}
          onPick={handlePick}
          onCommit={handleCommit} />
      )}
    </>
  )
}
